import { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SERVICE_INTERVAL = 12000; // typical UK annual service interval (mi)

/**
 * Set the vehicle's next-service reminder (date and/or mileage) from the job.
 * Saves to the vehicle via PATCH /vehicles/{id} — drives the Dashboard reminders.
 */
export function NextServiceCard({ vehicleId, baselineMileage, onSaved }) {
  const [date, setDate] = useState("");
  const [mileage, setMileage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vehicleId) return;
    api(`/vehicles/${vehicleId}`)
      .then((v) => {
        setDate(v.next_service_date || "");
        setMileage(v.next_service_mileage != null ? String(v.next_service_mileage) : "");
      })
      .catch(() => {});
  }, [vehicleId]);

  if (!vehicleId) return null;

  const save = async () => {
    if (!date && !mileage) {
      toast.error("Set a next-service date or mileage first");
      return;
    }
    setSaving(true);
    try {
      await api(`/vehicles/${vehicleId}`, {
        method: "PATCH",
        body: JSON.stringify({
          next_service_date: date || null,
          next_service_mileage: mileage ? Number(mileage) : null,
        }),
      });
      toast.success("Next-service reminder saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.message || "Could not save reminder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="next-service-card">
      <div className="mb-3 flex items-center gap-2">
        <BellRing className="h-4 w-4 text-brand" />
        <h3 className="font-display text-lg font-bold uppercase text-midnight">Next Service Reminder</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Set a due date and/or odometer target. The vehicle shows up on the Dashboard reminders when it&apos;s due.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">NEXT SERVICE DATE</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="next-service-date"
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">NEXT SERVICE MILEAGE</span>
          <input type="number" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="next-service-mileage"
            placeholder="e.g. 80000"
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
        </label>
      </div>
      {baselineMileage != null && (
        <button type="button" onClick={() => setMileage(String(baselineMileage + SERVICE_INTERVAL))} data-testid="next-service-interval-btn"
          className="mt-2 text-xs font-semibold text-brand-2 underline">
          Use last recorded {baselineMileage.toLocaleString()} mi + {SERVICE_INTERVAL.toLocaleString()} mi
        </button>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="next-service-save"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save Reminder
        </button>
      </div>
    </div>
  );
}
